<form [formGroup]="form" (ngSubmit)="onSubmit()" class="container py-2 py-md-3" @pageEnter>
  <div class="row justify-content-center">
    <div class="col-12 col-lg-9">
      <mat-card @cardEnter class="border-0 rounded-4 overflow-hidden shadow-lg bg-white bg-opacity-75">
        <div class="bg-primary bg-gradient text-white p-2 p-md-3" @staggerItems>
          <div class="d-flex align-items-center gap-2 anim-item">
            <mat-icon>auto_stories</mat-icon>
            <span class="fw-semibold">Vocabulary</span>
          </div>
          <div class="anim-item">
            <p class="text-white-50 small text-uppercase fw-semibold mb-0">
              {{ isEdit() ? 'Edit' : 'New word' }}
            </p>
            <h3 class="fw-bold mb-0 fs-6">
              {{ isEdit() ? 'Update Word' : 'Add to your vocabulary' }}
            </h3>
          </div>
        </div>

        <mat-card-content class="p-2 p-md-3">
          <div class="row g-2" @staggerItems>
            <div class="col-12 anim-item">
              <div class="d-flex align-items-center gap-1">
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="flex-fill" style="margin-bottom:0">
                  <mat-label>From</mat-label>
                  <mat-select formControlName="language" [compareWith]="compareByUuid">
                    @for (l of languages(); track l.uuid) {
                      <mat-option [value]="l">{{ l.name }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>

                <button
                  mat-icon-button
                  type="button"
                  color="primary"
                  class="flex-shrink-0"
                  (click)="swapLanguages()"
                  [disabled]="!form.value.language || !form.value.languageTo"
                  aria-label="Swap languages">
                  <mat-icon>swap_horiz</mat-icon>
                </button>

                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="flex-fill" style="margin-bottom:0">
                  <mat-label>To</mat-label>
                  <mat-select formControlName="languageTo" [compareWith]="compareByUuid">
                    @for (l of languages(); track l.uuid) {
                      <mat-option [value]="l">{{ l.name }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
            </div>

            <div class="col-12 anim-item">
              <div class="p-2 bg-white bg-opacity-75 border border-primary-subtle rounded-3 shadow-sm">
                <div class="row g-2">
                  <div class="col-12 col-md-6">
                    <mat-form-field appearance="fill" subscriptSizing="dynamic" class="w-100">
                      <mat-label>Sentence</mat-label>
                      <textarea
                        matInput
                        cdkTextareaAutosize
                        cdkAutosizeMinRows="1"
                        #sentenceInput
                        [formControl]="form.controls.sentence"
                        placeholder="e.g. The cat sat on the mat"
                        (input)="clearMessage()"
                      ></textarea>
                      @if (sentenceIsInvalid) {
                        <mat-error>Please enter a valid Sentence</mat-error>
                      }
                    </mat-form-field>
                  </div>

                  <div class="col-12 col-md-6">
                    <mat-form-field appearance="fill" subscriptSizing="dynamic" class="w-100">
                      <mat-label>Translation</mat-label>
                      <textarea
                        matInput
                        cdkTextareaAutosize
                        cdkAutosizeMinRows="1"
                        formControlName="translation"
                      ></textarea>
                      @if (translationIsInvalid) {
                        <mat-error>Please enter a valid Translation</mat-error>
                      }
                    </mat-form-field>
                  </div>

                  <div class="col-12 d-flex align-items-center">
                    <app-word-translation
                      [translation]="{
                        sentence: form.controls.sentence.value ?? '',
                        fromCode: form.controls.language.value?.code ?? null,
                        toCode: form.controls.languageTo.value?.code ?? null,
                      }"
                      (translated)="onTranslated($event)"
                    />
                  </div>

                  <div class="col-12">
                    <mat-form-field appearance="fill" subscriptSizing="dynamic" class="w-100">
                      <mat-label>Description</mat-label>
                      <input matInput formControlName="description" />
                    </mat-form-field>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 anim-item">
              <div class="d-flex align-items-center flex-wrap gap-1">
                <button
                  mat-button
                  type="button"
                  color="primary"
                  class="rounded-pill px-2 d-inline-flex align-items-center gap-1"
                  (click)="suggestTags()"
                  [disabled]="isSuggestingTags() || !form.value.sentence?.trim() || !form.value.language"
                >
                  @if (isSuggestingTags()) {
                    <ng-container>
                      <mat-spinner diameter="16" class="d-inline-block align-middle"></mat-spinner>
                      <span class="small">Suggesting…</span>
                    </ng-container>
                  } @else {
                    <ng-container>
                      <mat-icon class="fs-6">auto_awesome</mat-icon>
                      <span class="small">Suggest tags</span>
                    </ng-container>
                  }
                </button>

                @for (tag of suggestedTags(); track tag.tag) {
                  <button
                    type="button"
                    class="btn btn-sm rounded-pill"
                    [class.btn-primary]="isTagSelected(tag)"
                    [class.btn-outline-secondary]="!isTagSelected(tag)"
                    (click)="toggleTag(tag, $event)"
                  >
                    {{ tag.label }}
                  </button>
                }
              </div>
            </div>

            <div class="col-12 anim-item">
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <button
                  mat-raised-button
                  type="submit"
                  color="primary"
                  class="rounded-pill px-4 bg-gradient d-flex flex-grow-1 flex-sm-grow-0 align-items-center justify-content-center gap-1 btn btn-primary cursor-pointer"
                  [disabled]="
                    isSubmitting() ||
                    !form.value.sentence?.trim() ||
                    !form.value.translation?.trim() ||
                    !form.value.language ||
                    !form.value.languageTo
                  "
                >
                @if (isSubmitting()) {
                  <span class="d-inline-flex align-items-center gap-1">
                    <mat-spinner diameter="20" class="d-inline-block align-middle"></mat-spinner>
                    Saving…
                  </span>
                } @else {
                  <span>{{ isEdit() ? 'Update' : 'Add' }}</span>
                }
                </button>

                @if (isEdit()) {
                  <button
                    mat-stroked-button
                    type="button"
                    color="accent"
                    class="rounded-pill d-flex flex-grow-1 flex-sm-grow-0 align-items-center justify-content-center gap-1 btn btn-outline-secondary cursor-pointer"
                    (click)="onCancel()"
                  >
                    Cancel
                  </button>
                }
              </div>
            </div>
          </div>

          @if (message()) {
            <div class="mt-2 alert" [class.alert-danger]="isError()" [class.alert-success]="!isError()">
              {{ message() }}
            </div>
          }
        </mat-card-content>
      </mat-card>
    </div>
  </div>
</form>
